import React from 'react';
import {
    Membership,
    MembershipBillingFrequency,
    MembershipEnrollmentType,
    MembershipRolloverUsage,
    MembershipPackageExpiration,
    MembershipPackageExpirationUnit,
    MEMBERSHIP_CUSTOM_BILLING_MIN_DAYS,
    MEMBERSHIP_CUSTOM_BILLING_MAX_DAYS
} from '@spike/membership-model';
import { SelectableCard } from 'components/UI/V2/Forms/SelectableCard/SelectableCard';
import Button from 'components/UI/V2/Button/Button';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faBoxTaped, faRotate } from '@fortawesome/pro-regular-svg-icons';
import { createStyles, makeStyles, Theme, Typography } from '@material-ui/core';
import { Select } from 'components/UI/V3/Forms/Select/Select';
import { useMasterData } from 'hooks';
import { Label } from 'components/UI/V2/Forms/Label/Label';
import { Radio } from 'components/UI/V2/Forms/Radio/Radio';
import { CounterField } from 'components/UI/V2/CounterField/CounterField';
import { Input } from 'components/UI/V3/Forms/Input/Input';
import clsx from 'clsx';

interface MembershipTypeStepProps {
    membership: Membership;
    onNext: (
        type: MembershipEnrollmentType,
        billingFrequency: MembershipBillingFrequency | '',
        billingIntervalDays: number | null,
        rolloverUsage: MembershipRolloverUsage,
        rolloverBillingCycles: Membership['rolloverBillingCycles'],
        packageExpiration: MembershipPackageExpiration,
        packageExpirationValue: number | null,
        packageExpirationUnit: MembershipPackageExpirationUnit | null
    ) => void;
}

const useStyles = makeStyles((theme: Theme) =>
    createStyles({
        membershipTypeColumn: {
            'gap': 8,
            'display': 'flex',
            'alignItems': 'center',
            'flexDirection': 'column',

            [theme.breakpoints.up('md')]: {
                gap: 16,
                flexDirection: 'row'
            },

            '& > p': {
                fontSize: 18,
                fontWeight: 500
            }
        },
        subscriptionFrequencyContainer: {
            gap: 24,
            display: 'flex',
            flexDirection: 'column',

            [theme.breakpoints.up('md')]: {
                width: '47%'
            }
        },
        radioOption: {
            gap: 16,
            display: 'flex',
            flexDirection: 'column'
        },
        radioGroupContainer: {
            gap: 20,
            display: 'flex',
            flexDirection: 'column'
        },
        inlineRadioGroupContainer: {
            gap: 16,
            display: 'flex',
            paddingLeft: 24,
            flexDirection: 'column',

            [theme.breakpoints.up('md')]: {
                gap: 24,
                flexDirection: 'row'
            }
        },
        counterFieldContainer: {
            gap: 8,

            [theme.breakpoints.down('sm')]: {
                flexDirection: 'row'
            }
        },
        counterField: {
            gap: 4
        },
        packageExpirationContainer: {
            display: 'flex',
            alignItems: 'center',
            gap: 12,
            paddingLeft: 24,

            [theme.breakpoints.down('sm')]: {
                flexDirection: 'column',
                alignItems: 'flex-start',
                gap: 8
            }
        },
        packageExpirationLabel: {
            fontSize: 16,
            fontWeight: 400,
            color: '#000000'
        },
        packageExpirationInput: {
            width: 80,
            textAlign: 'center'
        },
        packageExpirationInputContainer: {
            width: 80
        },
        packageExpirationSelect: {
            minWidth: 120,
            maxWidth: 140
        },
        submitButton: {
            marginTop: 8,

            [theme.breakpoints.up('md')]: {
                alignSelf: 'flex-start'
            }
        }
    })
);

export const MembershipTypeStep: React.FC<MembershipTypeStepProps> = props => {
    const classes = useStyles();
    const masterdata = useMasterData();

    const [membershipType, setMembershipType] = React.useState(props.membership.enrollmentType);
    const [billingFrequency, setBillingFrequency] = React.useState(props.membership.billingFrequency);
    const [billingIntervalDays, setBillingIntervalDays] = React.useState<number | null>(
        props.membership.billingIntervalDays ?? null
    );
    const [rolloverUsage, setRolloverUsage] = React.useState(props.membership.rolloverUsage);
    const [rolloverBillingCycles, setRolloverBillingCycles] = React.useState(props.membership.rolloverBillingCycles);
    const [packageExpiration, setPackageExpiration] = React.useState<MembershipPackageExpiration>(
        (props.membership.packageExpiration as MembershipPackageExpiration) || MembershipPackageExpiration.NEVER
    );
    const [packageExpirationValue, setPackageExpirationValue] = React.useState<number | null>(
        props.membership.packageExpirationValue || 1
    );
    const [packageExpirationUnit, setPackageExpirationUnit] = React.useState<MembershipPackageExpirationUnit | null>(
        (props.membership.packageExpirationUnit as MembershipPackageExpirationUnit) ||
            MembershipPackageExpirationUnit.DAYS
    );

    const isValid = React.useMemo(() => {
        if (!membershipType) {
            return false;
        }

        if (membershipType === MembershipEnrollmentType.PACKAGE) {
            if (!packageExpiration) return false;
            if (packageExpiration === MembershipPackageExpiration.LIMITED) {
                if (!packageExpirationValue || !packageExpirationUnit) return false;
            }
            return true;
        }

        if (membershipType === MembershipEnrollmentType.SUBSCRIPTION && !billingFrequency) {
            return false;
        }

        if (
            membershipType === MembershipEnrollmentType.SUBSCRIPTION &&
            billingFrequency === MembershipBillingFrequency.CUSTOM &&
            (billingIntervalDays === null ||
                !Number.isInteger(billingIntervalDays) ||
                billingIntervalDays < MEMBERSHIP_CUSTOM_BILLING_MIN_DAYS ||
                billingIntervalDays > MEMBERSHIP_CUSTOM_BILLING_MAX_DAYS)
        ) {
            return false;
        }

        if (membershipType === MembershipEnrollmentType.SUBSCRIPTION && !rolloverUsage) {
            return false;
        }

        if (
            membershipType === MembershipEnrollmentType.SUBSCRIPTION &&
            billingFrequency &&
            rolloverUsage === MembershipRolloverUsage.LIMITED &&
            !rolloverBillingCycles
        ) {
            return false;
        }

        return true;
    }, [
        membershipType,
        billingFrequency,
        billingIntervalDays,
        rolloverUsage,
        rolloverBillingCycles,
        packageExpiration,
        packageExpirationValue,
        packageExpirationUnit
    ]);

    const nextHandler = () => {
        if (!membershipType) {
            return;
        }

        props.onNext(
            membershipType as MembershipEnrollmentType,
            billingFrequency as MembershipBillingFrequency,
            billingFrequency === MembershipBillingFrequency.CUSTOM ? billingIntervalDays : null,
            rolloverUsage as MembershipRolloverUsage,
            rolloverBillingCycles as Membership['rolloverBillingCycles'],
            packageExpiration,
            packageExpirationValue,
            packageExpirationUnit
        );
    };

    React.useEffect(() => {
        // Reset billing frequency and rollover usage if package enrollment type is selected
        if (membershipType === MembershipEnrollmentType.PACKAGE) {
            setBillingFrequency('' as MembershipBillingFrequency);
            setRolloverUsage('' as MembershipRolloverUsage);
            setRolloverBillingCycles(null);
        }
    }, [membershipType]);

    React.useEffect(() => {
        // Reset custom interval days when billing frequency is not custom
        if (billingFrequency !== MembershipBillingFrequency.CUSTOM) {
            setBillingIntervalDays(null);
        }
    }, [billingFrequency]);

    React.useEffect(() => {
        // Set rollover billing cycles to 1 if rollover usage is limited
        if (rolloverUsage === MembershipRolloverUsage.LIMITED && !rolloverBillingCycles) {
            setRolloverBillingCycles(1);
        }

        // Reset rollover billing cycles if rollover usage is not limited
        if (rolloverUsage !== MembershipRolloverUsage.LIMITED) {
            setRolloverBillingCycles(null);
        }
    }, [rolloverUsage]);

    React.useEffect(() => {
        // Set default values when package expiration is limited
        if (packageExpiration === MembershipPackageExpiration.LIMITED) {
            if (!packageExpirationValue) setPackageExpirationValue(1);
            if (!packageExpirationUnit) setPackageExpirationUnit(MembershipPackageExpirationUnit.DAYS);
        }

        // Reset values when never expires
        if (packageExpiration === MembershipPackageExpiration.NEVER) {
            setPackageExpirationValue(null);
            setPackageExpirationUnit(null);
        }
    }, [packageExpiration]);

    return (
        <>
            <div className={classes.membershipTypeColumn}>
                <SelectableCard
                    label="Package"
                    description="Set up packages with a single upfront payment."
                    icon={<FontAwesomeIcon icon={faBoxTaped} />}
                    checked={membershipType === MembershipEnrollmentType.PACKAGE}
                    onChange={() => setMembershipType(MembershipEnrollmentType.PACKAGE)}
                />

                <Typography>or</Typography>

                <SelectableCard
                    label="Subscription"
                    description="Setup recurring memberships with automatic payments."
                    icon={<FontAwesomeIcon icon={faRotate} />}
                    checked={membershipType === MembershipEnrollmentType.SUBSCRIPTION}
                    onChange={() => setMembershipType(MembershipEnrollmentType.SUBSCRIPTION)}
                />
            </div>

            {/* Show package expiration options if package is selected */}
            {membershipType === MembershipEnrollmentType.PACKAGE && (
                <>
                    <Label>When Does This Package Expire?</Label>

                    <div className={classes.radioGroupContainer}>
                        <Radio
                            inlineLabel="Never Expires"
                            description="Clients keep unused items forever"
                            checked={packageExpiration === MembershipPackageExpiration.NEVER}
                            onChange={checked => {
                                if (checked) {
                                    setPackageExpiration(MembershipPackageExpiration.NEVER);
                                }
                            }}
                        />

                        <div className={classes.radioOption}>
                            <Radio
                                inlineLabel="Expires"
                                description="Items must be used within a set time"
                                checked={packageExpiration === MembershipPackageExpiration.LIMITED}
                                onChange={checked => {
                                    if (checked) {
                                        setPackageExpiration(MembershipPackageExpiration.LIMITED);
                                    }
                                }}
                            />

                            {packageExpiration === MembershipPackageExpiration.LIMITED && (
                                <div className={classes.packageExpirationContainer}>
                                    <span className={classes.packageExpirationLabel}>After</span>

                                    <div className={classes.packageExpirationInputContainer}>
                                        <Input
                                            type="number"
                                            value={String(packageExpirationValue || 1)}
                                            onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                                                const value = parseInt(e.target.value);
                                                if (!isNaN(value) && value > 0) {
                                                    setPackageExpirationValue(value);
                                                }
                                            }}
                                            min={1}
                                            step={1}
                                            autoFocus={false}
                                            className={classes.packageExpirationInput}
                                        />
                                    </div>
                                    <Select
                                        value={packageExpirationUnit || MembershipPackageExpirationUnit.DAYS}
                                        options={[
                                            { label: 'Days', value: MembershipPackageExpirationUnit.DAYS },
                                            { label: 'Weeks', value: MembershipPackageExpirationUnit.WEEKS },
                                            { label: 'Months', value: MembershipPackageExpirationUnit.MONTHS },
                                            { label: 'Years', value: MembershipPackageExpirationUnit.YEARS }
                                        ]}
                                        onChange={value =>
                                            setPackageExpirationUnit(value as MembershipPackageExpirationUnit)
                                        }
                                        className={classes.packageExpirationSelect}
                                    />
                                </div>
                            )}
                        </div>
                    </div>
                </>
            )}

            {/* Show frequency options if subscription it's selected */}
            {membershipType === MembershipEnrollmentType.SUBSCRIPTION && (
                <>
                    <div className={classes.subscriptionFrequencyContainer}>
                        <Select
                            value={billingFrequency}
                            label="Billing Frequency"
                            placeholder="Select Billing Frequency"
                            options={masterdata.enrollmentBillingFrequencies.map(frequency => ({
                                label: frequency.name,
                                value: frequency.id
                            }))}
                            onChange={value => {
                                setBillingFrequency(value as MembershipBillingFrequency);
                                //****************************************************
                                //Remove this when the rollover usage is implemented
                                //setRolloverUsage(MembershipRolloverUsage.RESET);
                                //****************************************************
                                //****************************************************
                            }}
                        />

                        {billingFrequency === MembershipBillingFrequency.CUSTOM && (
                            <div className={classes.packageExpirationContainer}>
                                <span className={classes.packageExpirationLabel}>Every</span>

                                <div className={classes.packageExpirationInputContainer}>
                                    <Input
                                        type="number"
                                        value={billingIntervalDays === null ? '' : String(billingIntervalDays)}
                                        placeholder="28"
                                        onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                                            if (e.target.value === '') {
                                                setBillingIntervalDays(null);
                                                return;
                                            }
                                            const value = parseInt(e.target.value);
                                            if (!isNaN(value)) {
                                                setBillingIntervalDays(value);
                                            }
                                        }}
                                        min={MEMBERSHIP_CUSTOM_BILLING_MIN_DAYS}
                                        max={MEMBERSHIP_CUSTOM_BILLING_MAX_DAYS}
                                        step={1}
                                        autoFocus={false}
                                        className={classes.packageExpirationInput}
                                    />
                                </div>

                                <span className={classes.packageExpirationLabel}>days</span>
                            </div>
                        )}
                    </div>

                    {/* Show rollover options if billing frequency is selected */}
                    {billingFrequency && (
                        <>
                            <Label>Customize Your Subscription Usage</Label>

                            <div className={classes.radioGroupContainer}>
                                <div className={classes.radioOption}>
                                    <Radio
                                        inlineLabel="Rollover"
                                        description="Unused items rollover to the next cycle"
                                        checked={[
                                            MembershipRolloverUsage.INDEFINITELY,
                                            MembershipRolloverUsage.LIMITED
                                        ].includes(rolloverUsage as MembershipRolloverUsage)}
                                        onChange={checked => {
                                            if (checked) {
                                                setRolloverUsage(MembershipRolloverUsage.INDEFINITELY);
                                            }
                                        }}
                                    />

                                    {rolloverUsage && rolloverUsage !== MembershipRolloverUsage.RESET && (
                                        <div className={classes.inlineRadioGroupContainer}>
                                            <Radio
                                                inlineLabel="Indefinitely"
                                                checked={rolloverUsage === MembershipRolloverUsage.INDEFINITELY}
                                                onChange={checked => {
                                                    if (checked) {
                                                        setRolloverUsage(MembershipRolloverUsage.INDEFINITELY);
                                                    }
                                                }}
                                            />

                                            <div className={classes.radioOption}>
                                                <Radio
                                                    inlineLabel="For a Set Number of Cycles"
                                                    checked={rolloverUsage === MembershipRolloverUsage.LIMITED}
                                                    onChange={checked => {
                                                        if (checked) {
                                                            setRolloverUsage(MembershipRolloverUsage.LIMITED);
                                                        }
                                                    }}
                                                />

                                                {rolloverUsage === MembershipRolloverUsage.LIMITED && (
                                                    <div
                                                        className={clsx(
                                                            classes.inlineRadioGroupContainer,
                                                            classes.counterFieldContainer
                                                        )}
                                                    >
                                                        <CounterField
                                                            minLimit={1}
                                                            value={Number(rolloverBillingCycles)}
                                                            className={classes.counterField}
                                                            onChange={value => setRolloverBillingCycles(value)}
                                                        />

                                                        <span>billing cycles</span>
                                                    </div>
                                                )}
                                            </div>
                                        </div>
                                    )}
                                </div>
                                <Radio
                                    inlineLabel="Reset"
                                    description="Resets with your billing cycle"
                                    checked={rolloverUsage === MembershipRolloverUsage.RESET}
                                    onChange={checked => {
                                        if (checked) {
                                            setRolloverUsage(MembershipRolloverUsage.RESET);
                                        }
                                    }}
                                />
                            </div>
                        </>
                    )}
                </>
            )}

            <Button
                label="Next"
                size="large"
                noColorOnDisabled
                disabled={!isValid}
                className={classes.submitButton}
                onClick={nextHandler}
            />
        </>
    );
};
